// ============================================================
// TERROR TCG — COMPANION MENAGERIE
// The third tab of the Pack Shop. Browse, preview, and equip
// companions. Swapping costs Sol Converter (Crests by tier).
//
// Props:
//   profile    — full player profile (expects uid, chocolateCoins,
//                equippedCompanion, companions, collection, starterDeckId)
//   onRefresh  — callback to re-fetch profile after equip
//
// Visual design:
//   - Tier filter chips (ALL / Escling / Beb / Champion / Lord / Mythic)
//   - Grid of companion tiles with lock states
//   - Detail panel slides in when tile clicked
//   - Sol Convert confirm shows cost, validates Crests, equips
//   - Equipped companion gets a gold halo and "Equipped" ribbon
// ============================================================

import { useState, useMemo } from "react";
import {
  COMPANIONS,
  TIERS,
  SOL_CONVERTER_COSTS,
  ILK_COLORS,
  getSolConverterCost,
  isCompanionUnlocked,
  getUnlockHint,
  getCompanionProvenance,
  getCompanionOrigins,
  getCompanionShopPrice,
  hasShopCompanion,
} from "./companions";
// ♛ KING'S GRACE (2026-09-16) — the Radiant boutique that lived here is
// retired. Graces are BOUGHT at the Foil Vault (GraceVault.jsx) and WORN
// here. Every legacy Radiant still counts as owned (kingsGrace reads it).
import {
  GRACES, GRACE_SOURCES, gracesFor, ownedGraces, equippedGraceOf, graceMarkRecord,
} from "./kingsGrace";
import SourceSigil from "./SourceSigil";
import CompanionSprite from "./CompanionSprite";
import TheMark from "./TheMark";

// ── Manuscript palette (matches rest of Plaza UI chrome) ────
const GM = {
  page:          "#0f0c07",
  pageWarm:      "#1a1509",
  pageMid:       "#241c0c",
  pageLight:     "#2e2410",
  pageBorder:    "#3d2e14",
  inkGold:       "#c8963c",
  inkGoldBright: "#e8b84b",
  inkGoldDim:    "#8a6520",
  inkBrown:      "#8a6a30",
  inkRed:        "#8b2020",
  vellum:        "#e8d5a3",
  vellumDim:     "#b89a5a",
  vellumFaint:   "#7a6040",
  font: "'Cinzel', 'Palatino Linotype', serif",
};

// ── Tier display order + labels ─────────────────────────────
const TIER_ORDER = [
  { id: "all",       label: "◆ All" },
  { id: "beb",       label: "Bebs" },
  { id: "champion",  label: "Champions" },
  { id: "lord",      label: "Lords" },
  { id: "mythic",    label: "Mythic" },
];

// ─────────────────────────────────────────────────────────────
// MAIN — Companion Menagerie tab
// ─────────────────────────────────────────────────────────────
export default function CompanionMenagerie({ profile, onRefresh }) {
  const [filterTier, setFilterTier] = useState("all");
  const [selected,   setSelected]   = useState(null); // companion object
  const [confirming, setConfirming] = useState(false);
  const [msg,        setMsg]        = useState("");
  const [equipping,  setEquipping]  = useState(false);
  const [radiantBusy, setRadiantBusy] = useState(null);

  const uid     = profile?.uid;
  const coins   = profile?.chocolateCoins || 0;
  const equipped = profile?.equippedCompanion || null;
  // Validated: a worn Grace the profile does not hold reads as nothing.
  const wornGrace = equippedGraceOf(profile);
  const equippedRadiant = wornGrace ? wornGrace.graceId : null;

  // ── 🐾 Venus' Pet Shop — buy any companion ────────────────
  const [shopBusy, setShopBusy] = useState(null);
  const handleBuyCompanion = async (companion) => {
    if (!uid || shopBusy) return;
    setShopBusy(companion.id);
    setMsg("");
    try {
      const { buyCompanion } = await import("./progression");
      const r = await buyCompanion(uid, companion.id);
      setMsg(r.success
        ? (r.alreadyOwned ? `🐾 You already bought ${companion.name} from Venus.` : `🐾 ${companion.name} is yours — Venus wraps it in tissue paper. Sol Convert to walk with it.`)
        : (r.reason || "Venus shakes her head. Try again."));
      if (r.success && onRefresh) onRefresh();
    } catch (e) { setMsg("Venus shakes her head. Try again."); }
    setShopBusy(null);
  };

  // ── Wear / take off a Grace (never swaps the companion) ────
  const handleWearGrace = async (companionId, graceId, source) => {
    if (!uid) return;
    setRadiantBusy(`${companionId}:${graceId}:${source}`);
    setMsg("");
    try {
      const { equipKingsGrace } = await import("./progression");
      const result = await equipKingsGrace(uid, companionId, graceId, source);
      if (result.success) {
        const g = graceId ? GRACES[graceId] : null;
        setMsg(g ? `♛ Now wearing ${g.label} — ${g.crown}.` : "Grace taken off.");
        if (onRefresh) onRefresh();
      } else {
        setMsg(result.reason || "Could not wear that Grace.");
      }
    } catch (e) { setMsg("Could not wear that Grace. Try again."); }
    setRadiantBusy(null);
  };

  // Build the full roster sorted by tier then name
  const roster = useMemo(() => {
    const tierRank = { escling: 0, beb: 1, champion: 2, lord: 3, mythic: 4 };
    const list = Object.values(COMPANIONS);
    list.sort((a, b) => {
      const ta = tierRank[a.tier] ?? 99;
      const tb = tierRank[b.tier] ?? 99;
      if (ta !== tb) return ta - tb;
      return a.name.localeCompare(b.name);
    });
    return list;
  }, []);

  const filtered = filterTier === "all"
    ? roster
    : roster.filter(c => c.tier === filterTier);

  // Counts for the tier chip badges
  const counts = useMemo(() => {
    const out = { all: { owned: 0, total: 0 } };
    for (const t of Object.keys(tierRankLookup)) out[t] = { owned: 0, total: 0 };
    for (const c of roster) {
      const unlocked = isCompanionUnlocked(profile, c.id);
      out.all.total++;
      if (unlocked) out.all.owned++;
      if (!out[c.tier]) out[c.tier] = { owned: 0, total: 0 };
      out[c.tier].total++;
      if (unlocked) out[c.tier].owned++;
    }
    return out;
  }, [profile, roster]);

  // ── Equip handler ──────────────────────────────────────────
  const handleConfirmEquip = async () => {
    if (!selected || !uid) return;
    setEquipping(true);
    setMsg("");
    try {
      const { equipCompanion } = await import("./progression");
      const result = await equipCompanion(uid, selected.id);
      if (result.success) {
        if (result.alreadyEquipped) {
          setMsg("✦ Already your companion.");
        } else {
          setMsg(`✦ ${selected.name} walks with you now. ${result.cost > 0 ? `(-${result.cost} Crests)` : ""}`);
          if (onRefresh) onRefresh();
        }
        setConfirming(false);
      } else {
        setMsg(result.reason || "Sol Convert failed.");
      }
    } catch (e) {
      console.error("[equipCompanion]", e);
      setMsg("Sol Convert failed. Try again.");
    }
    setEquipping(false);
  };

  // ── Detail-panel open handler ─────────────────────────────
  const handleSelect = (companion) => {
    setSelected(companion);
    setConfirming(false);
    setMsg("");
  };

  // ─────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────
  return (
    <div style={{ width: "100%" }}>

      {/* ── Tier filter chips ─────────────────────────────── */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: 6,
        marginBottom: 14, justifyContent: "center",
      }}>
        {TIER_ORDER.map(t => {
          const isActive = filterTier === t.id;
          const cnt = counts[t.id] || { owned: 0, total: 0 };
          return (
            <button
              key={t.id}
              onClick={() => { setFilterTier(t.id); setSelected(null); }}
              style={{
                padding: "6px 12px",
                background: isActive
                  ? `linear-gradient(135deg, ${GM.pageMid}, ${GM.pageLight})`
                  : GM.pageWarm,
                border: `1px solid ${isActive ? GM.inkGold : GM.inkBrown + "66"}`,
                borderRadius: 14, cursor: "pointer",
                color: isActive ? GM.inkGoldBright : GM.vellumFaint,
                fontFamily: GM.font, fontSize: 10, fontWeight: 900,
                letterSpacing: 1, whiteSpace: "nowrap",
                transition: "all 0.15s",
              }}
            >
              {t.label} <span style={{
                color: isActive ? GM.vellumDim : GM.vellumFaint,
                fontWeight: 400, marginLeft: 4,
              }}>{cnt.owned}/{cnt.total}</span>
            </button>
          );
        })}
      </div>

      {/* ── Sol Converter cost legend ─────────────────────── */}
      <div style={{
        textAlign: "center",
        fontSize: 9, color: GM.vellumFaint, letterSpacing: 2,
        marginBottom: 12, fontFamily: GM.font,
      }}>
        ◆ SOL CONVERTER · Beb {SOL_CONVERTER_COSTS.beb}¢ · Champion {SOL_CONVERTER_COSTS.champion}¢ · Lord {SOL_CONVERTER_COSTS.lord}¢ · Mythic {SOL_CONVERTER_COSTS.mythic}¢ ◆
      </div>

      {/* ── Status message ────────────────────────────────── */}
      {msg && (
        <div style={{
          background: GM.pageMid, border: `1px solid ${GM.inkGold}`,
          color: GM.inkGoldBright, padding: "8px 14px", borderRadius: 2,
          fontSize: 11, marginBottom: 12, textAlign: "center",
          fontStyle: "italic", fontFamily: GM.font,
        }}>{msg}</div>
      )}

      {/* ── Companion grid ────────────────────────────────── */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(92px, 1fr))",
        gap: 8,
      }}>
        {filtered.map(c => (
          <CompanionTile
            key={c.id}
            companion={c}
            unlocked={isCompanionUnlocked(profile, c.id)}
            equipped={equipped === c.id}
            radiantTier={equipped === c.id ? equippedRadiant : null}
            onClick={() => handleSelect(c)}
            isSelected={selected?.id === c.id}
          />
        ))}
      </div>

      {/* ── Detail / Sol-Convert panel ─────────────────────── */}
      {selected && (
        <CompanionDetailPanel
          companion={selected}
          profile={profile}
          equipped={equipped === selected.id}
          unlocked={isCompanionUnlocked(profile, selected.id)}
          coins={coins}
          confirming={confirming}
          equipping={equipping}
          onConfirm={() => setConfirming(true)}
          onDoEquip={handleConfirmEquip}
          onBuy={() => handleBuyCompanion(selected)}
          buying={shopBusy === selected.id}
          onCancel={() => { setConfirming(false); setSelected(null); }}
        />
      )}

      {/* ── King's Grace wardrobe ─────────────────────────── */}
      <GraceWardrobe
        profile={profile}
        equipped={equipped}
        worn={wornGrace}
        busyKey={radiantBusy}
        onWear={handleWearGrace}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ♛ GRACE WARDROBE — every Grace you hold. The ones for the companion
// at your side can be worn; the rest say who they belong to. Buying
// happens at the Foil Vault, never here.
// ─────────────────────────────────────────────────────────────
function GraceWardrobe({ profile, equipped, worn, busyKey, onWear }) {
  // a retired foil (Shadow) is still OWNED but draws nothing — list only
  // what can be shown
  const all = ownedGraces(profile).filter((g) => GRACES[g.graceId]);
  const mine = equipped ? gracesFor(profile, equipped).filter((g) => GRACES[g.graceId]) : [];
  const others = all.filter((g) => g.companionId !== equipped);
  const byCompanion = {};
  for (const g of others) (byCompanion[g.companionId] = byCompanion[g.companionId] || []).push(g);

  return (
    <div style={{ marginTop: 30 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, transparent, #c8963c44)" }} />
        <div style={{ fontSize: 11, color: GM.inkGoldBright, letterSpacing: 4, fontWeight: 900, fontFamily: GM.font }}>
          ♛ KING'S GRACE ♛
        </div>
        <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, #c8963c44, transparent)" }} />
      </div>
      <div style={{ fontSize: 9.5, color: GM.vellumFaint, textAlign: "center", marginBottom: 14, fontStyle: "italic", fontFamily: GM.font, lineHeight: 1.6 }}>
        The foil, worn as light. Graces are sold at the Foil Vault and earned in the story;
        a Grace from Annihilation is worn here too.<br/>
        {worn && (
          <button onClick={() => onWear(equipped, null, null)}
            style={{ marginTop: 6, background: "transparent", border: `1px solid ${GM.inkBrown}66`,
              borderRadius: 10, color: GM.vellumDim, fontSize: 9, padding: "3px 10px",
              cursor: "pointer", fontFamily: GM.font, letterSpacing: 1 }}>
            ◇ Take off {GRACES[worn.graceId]?.label}
          </button>
        )}
      </div>

      {all.length === 0 ? (
        <div style={{ textAlign: "center", padding: "16px", color: GM.vellumFaint, fontSize: 10,
          fontStyle: "italic", border: `1px dashed ${GM.pageBorder}`, borderRadius: 8, fontFamily: GM.font }}>
          No Graces yet. The Foil Vault sells one for every companion.
        </div>
      ) : (
        <>
          {mine.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(108px, 1fr))", gap: 10, marginBottom: 16 }}>
              {mine.map((c) => (
                <GraceCopyTile key={c.key} copy={c} wearable
                  isWearing={!!worn && worn.graceId === c.graceId && worn.source === c.source}
                  busy={busyKey === `${c.companionId}:${c.graceId}:${c.source}`}
                  onWear={() => onWear(c.companionId, c.graceId, c.source)} />
              ))}
            </div>
          )}
          {Object.entries(byCompanion).map(([cid, copies]) => (
            <div key={cid} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 9, letterSpacing: 2, color: GM.vellumDim, fontFamily: GM.font, marginBottom: 6 }}>
                FOR {(COMPANIONS[cid]?.name || cid).toUpperCase()} — {isCompanionUnlocked(profile, cid) ? "Sol Convert to wear" : "unlock it to wear"}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(108px, 1fr))", gap: 10 }}>
                {copies.map((c) => <GraceCopyTile key={c.key} copy={c} />)}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function GraceCopyTile({ copy, wearable = false, isWearing = false, busy = false, onWear }) {
  const g = GRACES[copy.graceId];
  const src = GRACE_SOURCES[copy.source];
  if (!g) return null;
  return (
    <div style={{
      background: `linear-gradient(160deg, ${g.color}0e, ${GM.pageMid})`,
      border: `1px solid ${isWearing ? g.color : g.color + "44"}`,
      borderRadius: 10, padding: "10px 8px 8px",
      display: "flex", flexDirection: "column", alignItems: "center", gap: 5,
      boxShadow: isWearing ? `0 0 14px ${g.color}66` : "none",
      fontFamily: GM.font, position: "relative", opacity: wearable ? 1 : 0.8,
    }}>
      <div style={{ position: "absolute", top: 4, left: 4 }} title={src?.label}>
        <SourceSigil source={src?.sigil || "grace_vault"} size={16} />
      </div>
      {isWearing && (
        <div style={{ position: "absolute", top: 4, right: 4, fontSize: 7, fontWeight: 900,
          color: g.color, letterSpacing: 1, background: "rgba(0,0,0,0.6)", padding: "1px 5px", borderRadius: 6 }}>
          WORN
        </div>
      )}
      <div style={{ position: "relative", isolation: "isolate" }}>
        <CompanionSprite companionId={copy.companionId} size={58} radiantTier={copy.graceId} still={!isWearing} />
      </div>
      <div style={{ fontSize: 8.5, fontWeight: 900, color: g.color, letterSpacing: 1, textAlign: "center" }}>
        {g.glyph} {g.label.toUpperCase()}
      </div>
      <div style={{ fontSize: 7.5, color: GM.vellumFaint, textAlign: "center" }}>{src?.short || copy.source}</div>
      {wearable && (
        <button onClick={!busy && !isWearing ? onWear : undefined}
          style={{
            width: "100%", padding: "5px", borderRadius: 7,
            cursor: isWearing ? "default" : "pointer",
            background: isWearing ? "transparent" : `${g.color}1e`,
            border: `1px solid ${g.color}${isWearing ? "44" : ""}`,
            color: g.color, fontSize: 9, fontWeight: 900, fontFamily: GM.font,
          }}>
          {isWearing ? "♛ Worn" : busy ? "..." : "Wear"}
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// COMPANION TILE — single grid entry
// ─────────────────────────────────────────────────────────────
function CompanionTile({ companion, unlocked, equipped, radiantTier, onClick, isSelected }) {
  const ilk = ILK_COLORS[companion.ilk] || ILK_COLORS.Neutral;
  const borderColor = equipped ? GM.inkGoldBright
                    : isSelected ? GM.inkGold
                    : unlocked ? GM.inkBrown
                    : GM.pageBorder;
  const borderWidth = equipped || isSelected ? 2 : 1;

  return (
    <button
      onClick={onClick}
      style={{
        position: "relative",
        padding: "10px 6px 8px",
        background: unlocked
          ? `linear-gradient(160deg, ${GM.pageWarm}, ${GM.pageMid})`
          : `linear-gradient(160deg, #0a0806, ${GM.pageWarm})`,
        border: `${borderWidth}px solid ${borderColor}`,
        borderRadius: 6, cursor: "pointer",
        display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
        transition: "all 0.15s",
        boxShadow: equipped
          ? `0 0 14px ${GM.inkGold}66, inset 0 0 12px ${GM.inkGold}22`
          : isSelected
          ? `0 0 10px ${GM.inkGold}44`
          : "none",
        fontFamily: GM.font,
      }}
    >
      {/* Equipped ribbon */}
      {equipped && (
        <div style={{
          position: "absolute", top: 4, right: 4,
          fontSize: 8, color: GM.inkGoldBright, letterSpacing: 1,
          background: "rgba(0,0,0,0.6)", padding: "1px 4px", borderRadius: 2,
          fontWeight: 900,
        }}>◆</div>
      )}

      {/* Sprite */}
      <div style={{ width: 56, height: 56, opacity: unlocked ? 1 : 0.65 }}>
        <CompanionSprite
          companionId={companion.id}
          size={56}
          locked={!unlocked}
          radiantTier={radiantTier || null}
          still
        />
      </div>

      {/* Name */}
      <div style={{
        fontSize: 9, lineHeight: 1.1, textAlign: "center",
        color: unlocked ? GM.vellum : GM.vellumFaint,
        fontWeight: unlocked ? 700 : 400,
        letterSpacing: 0.5,
        minHeight: 22,
      }}>
        {unlocked ? companion.name : "???"}
      </div>

      {/* Tier tag */}
      <div style={{
        fontSize: 7, letterSpacing: 2,
        color: unlocked ? ilk.primary : GM.vellumFaint,
        opacity: 0.8, textTransform: "uppercase", fontWeight: 900,
      }}>
        {companion.tier}
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// DETAIL PANEL — appears below grid when a tile is selected
// ─────────────────────────────────────────────────────────────
function CompanionDetailPanel({
  companion, profile, equipped, unlocked, coins,
  confirming, equipping, onConfirm, onDoEquip, onCancel,
  onBuy, buying = false,
}) {
  const shopPrice = getCompanionShopPrice(companion.id);
  const shopOwned = hasShopCompanion(profile, companion.id);
  const canShop = !shopOwned && shopPrice != null && coins >= shopPrice && !buying;
  const cost = getSolConverterCost(companion.id);
  const canAfford = coins >= cost;
  const ilk = ILK_COLORS[companion.ilk] || ILK_COLORS.Neutral;

  // ── THE MARK (H18, doctrine §13) — the companion's Empire record.
  // Unlocked companions only (a "???" being prints no Mark — the
  // Empire hasn't processed what you haven't met). Origin honors the
  // companion lore law via originLore (never a literal source line);
  // OWNED radiant sheens list under FOIL · AURA as the foil ledger.
  const [showMark, setShowMark] = useState(false);
  const prov = unlocked ? getCompanionProvenance(companion.id, profile) : null;
  // Every way you hold it, rarest first — story/starter/card before the
  // pet shop. The first prints as the origin; the rest sit beneath it.
  const origins = unlocked ? getCompanionOrigins(companion.id, profile) : [];
  const originRecord = origins.length ? { sources: Object.fromEntries(origins.map((o) => [o, 1])) } : null;
  // King's Grace copies print in their own Mark section, each with the
  // sigil of where it came from (Vault / Story / Old Fold).
  const graceRecord = unlocked ? graceMarkRecord(profile, companion.id) : null;

  return (
    <div style={{
      marginTop: 16, padding: "16px 18px",
      background: `linear-gradient(160deg, ${GM.pageMid}, ${GM.pageWarm})`,
      border: `1px solid ${GM.inkGold}66`,
      borderRadius: 8,
      fontFamily: GM.font,
      display: "flex", gap: 14, alignItems: "flex-start",
    }}>
      {/* Large sprite */}
      <div style={{ flexShrink: 0, width: 88, height: 88 }}>
        <CompanionSprite companionId={companion.id} size={88} locked={!unlocked} />
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 8, letterSpacing: 3, color: ilk.primary,
          fontWeight: 900, textTransform: "uppercase", marginBottom: 2,
        }}>
          {companion.tier} · {companion.ilk}
        </div>
        <div style={{
          fontSize: 15, fontWeight: 900, color: unlocked ? GM.inkGoldBright : GM.vellumDim,
          fontStyle: "italic", marginBottom: 6,
        }}>
          {companion.name}
        </div>
        <div style={{
          fontSize: 10, color: unlocked ? GM.vellum : GM.vellumFaint,
          fontStyle: "italic", lineHeight: 1.5, marginBottom: 10,
        }}>
          {unlocked ? companion.description : getUnlockHint(companion.id)}
        </div>

        {/* Action row */}
        {unlocked && !equipped && !confirming && (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              onClick={onConfirm}
              disabled={!canAfford}
              style={{
                padding: "8px 16px",
                background: canAfford
                  ? `linear-gradient(135deg, ${GM.pageMid}, #3b0f6e)`
                  : GM.pageWarm,
                border: `1px solid ${canAfford ? GM.inkGold : GM.inkBrown}66`,
                borderRadius: 6,
                color: canAfford ? GM.inkGoldBright : GM.vellumFaint,
                fontFamily: GM.font, fontSize: 11, fontWeight: 900,
                letterSpacing: 1, cursor: canAfford ? "pointer" : "not-allowed",
                boxShadow: canAfford ? `0 0 12px ${GM.inkGold}33` : "none",
              }}
            >
              ◆ Sol Convert — {cost}¢
            </button>
            <span style={{ fontSize: 9, color: GM.vellumFaint, letterSpacing: 1 }}>
              {canAfford ? "Swap your companion" : "Not enough Coins"}
            </span>
          </div>
        )}

        {/* Confirm dialog */}
        {unlocked && !equipped && confirming && (
          <div style={{
            padding: "10px 12px",
            background: "rgba(0,0,0,0.4)",
            border: `1px solid ${GM.inkGold}44`, borderRadius: 4,
            display: "flex", flexDirection: "column", gap: 8,
          }}>
            <div style={{ fontSize: 10, color: GM.vellum, lineHeight: 1.5 }}>
              Sol Convert into <span style={{ color: GM.inkGoldBright, fontWeight: 900 }}>{companion.name}</span>?
              This will cost <span style={{ color: GM.inkGoldBright, fontWeight: 900 }}>{cost}</span> Chocolate Coins.
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={onDoEquip}
                disabled={equipping || !canAfford}
                style={{
                  padding: "8px 14px",
                  background: `linear-gradient(135deg, ${GM.pageMid}, #3b0f6e)`,
                  border: `1px solid ${GM.inkGold}`, borderRadius: 6,
                  color: GM.inkGoldBright, fontFamily: GM.font,
                  fontSize: 11, fontWeight: 900, cursor: equipping ? "wait" : "pointer",
                  opacity: equipping ? 0.6 : 1,
                }}
              >{equipping ? "◆ Converting..." : "◆ Confirm"}</button>
              <button
                onClick={onCancel}
                style={{
                  padding: "8px 14px",
                  background: "transparent",
                  border: `1px solid ${GM.inkBrown}66`, borderRadius: 6,
                  color: GM.vellumFaint, fontFamily: GM.font,
                  fontSize: 11, cursor: "pointer",
                }}
              >Cancel</button>
            </div>
          </div>
        )}

        {/* Equipped state */}
        {equipped && (
          <div style={{
            display: "inline-block",
            padding: "6px 12px",
            background: `${GM.inkGold}22`,
            border: `1px solid ${GM.inkGold}`,
            borderRadius: 4,
            fontSize: 10, color: GM.inkGoldBright, letterSpacing: 2,
            fontWeight: 900,
          }}>
            ◆ EQUIPPED
          </div>
        )}

        {/* Locked state */}
        {!unlocked && (
          <div style={{
            display: "inline-block",
            padding: "6px 12px",
            background: "rgba(0,0,0,0.4)",
            border: `1px solid ${GM.inkBrown}66`,
            borderRadius: 4,
            fontSize: 10, color: GM.vellumFaint, letterSpacing: 1,
            fontStyle: "italic",
          }}>
            ◆ Locked
          </div>
        )}

        {/* ── 🐾 VENUS' PET SHOP — every companion is for sale ── */}
        <div style={{
          marginTop: 12, padding: "8px 10px", borderRadius: 6,
          border: "1px solid #f2a6c855", background: "rgba(242,166,200,0.06)",
          display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        }}>
          <SourceSigil source="companion_shop" size={26} />
          <div style={{ flex: 1, minWidth: 140 }}>
            <div style={{ fontSize: 10, fontWeight: 900, color: "#f2a6c8", letterSpacing: 1.5 }}>VENUS' PET SHOP</div>
            <div style={{ fontSize: 9, color: GM.vellumFaint, fontStyle: "italic", lineHeight: 1.4 }}>
              {shopOwned
                ? "You bought this one from Venus — it wears her tag on its Mark."
                : unlocked
                  ? "A shop copy wears Venus' tag beside the mark it already has."
                  : "Skip the hunt — Venus has one in the back. The story's copy is still the rarer mark."}
            </div>
          </div>
          {shopOwned ? (
            <span style={{ fontSize: 10, fontWeight: 900, color: "#f2a6c8", letterSpacing: 1 }}>🐾 BOUGHT</span>
          ) : (
            <button onClick={canShop ? onBuy : undefined} disabled={!canShop}
              style={{
                padding: "7px 12px", borderRadius: 6, fontFamily: GM.font, fontSize: 11, fontWeight: 900,
                background: canShop ? "rgba(242,166,200,0.14)" : "transparent",
                border: `1px solid ${canShop ? "#f2a6c8" : GM.pageBorder}`,
                color: canShop ? "#f2a6c8" : GM.vellumFaint, cursor: canShop ? "pointer" : "not-allowed",
              }}>
              {buying ? "…" : shopPrice == null ? "—" : `🐾 Buy — ${shopPrice}¢`}
            </button>
          )}
        </div>

        {/* ── THE MARK — the Empire's record on this companion ── */}
        {unlocked && (
          <>
            <button onClick={() => setShowMark(m => !m)} style={{
              display: "block", width: "100%", marginTop: 12, padding: "8px",
              background: showMark ? `${GM.inkGold}18` : "transparent",
              border: `1px solid ${GM.inkGold}66`,
              borderRadius: 4, cursor: "pointer", color: GM.inkGold,
              fontFamily: GM.font, fontSize: 10, fontWeight: 900, letterSpacing: 2.5,
            }}>▦ THE MARK</button>
            {showMark && (
              <TheMark
                being={{ key: companion.id, name: companion.name, type: `${companion.tier} Companion · ${companion.ilk}`, game: "Terror" }}
                record={originRecord}
                graces={graceRecord ? graceRecord.graces : []}
                origins={origins.length ? origins : (prov?.sigil ? [prov.sigil] : [])}
                originLore={prov?.lore || null}
                width="100%"
                style={{ marginTop: 10 }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Tier rank lookup for counts (outside component, pure) ───
const tierRankLookup = { escling: 0, beb: 1, champion: 2, lord: 3, mythic: 4 };
